import * as React from 'react';
import { useEffect, useState } from 'react';
import RemoteSearchSelectProps from './RemoteSearchSelect.types';
import useDebounce from '../../shared/utils/debounceHook';
import { SearchOption } from '../../atoms/SearchInput/SearchInput.types';
import SearchInput from '../../atoms/SearchInput';

const delayTyping = 500;

const searchProps = {
  searchable: true,
  remote: true,
};

const RemoteSearchSelect = ({
  searchUrl,
  keyName,
  valueToShow,
  emptyOption,
  placeholder,
  queryParamName = 'name',
  additionalQueryParams = {},
  onChange = () => {},
  processResults,
  onChangeInputText,
  onRegexSuccess,
  onRegexFailure,
  serviceCaller,
  regex,
  dataCy = 'remoteSearchSelect',
  autoComplete,
  invalid,
  ariaLabel,
  errorMsgHolderId,
}: RemoteSearchSelectProps) => {
  const [optionList, setOptionList] = useState<SearchOption[]>([]);
  const [selectValue, setSelectValue] = useState<string>('');
  const [optionListDisabled, setOptionListDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearchResponse = async (response) => {
    let { data } = response;
    if (data.length === 0) {
      setOptionList([]);
    } else {
      if (processResults) {
        data = await processResults(data);
      }
      setOptionList(data);
      setOptionListDisabled(false);
    }
    setIsLoading(false);
  };

  const remoteSearch = (text: string) => {
    const param = text.toLowerCase();

    if (regex && !RegExp(regex).test(param)) {
      setIsLoading(false);
      setOptionList([]);

      if (onRegexFailure) {
        onRegexFailure();
      }

      return;
    }

    if (onRegexSuccess) {
      onRegexSuccess();
    }

    serviceCaller({
      url: searchUrl,
      method: 'get',
      query: {
        [queryParamName]: param,
        ...additionalQueryParams,
      },
      onSuccessCallback: handleSearchResponse,
    });
  };

  const debouncedSearchTerm = useDebounce(selectValue, delayTyping);

  useEffect(() => {
    if (selectValue?.length > 0) {
      remoteSearch(selectValue);
    } else {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm]);

  const onSelectChange = (value) => {
    const selectedOption = optionList.find((option: any) => (keyName ? option[keyName] === value : option === value));
    onChange(selectedOption);
    setIsLoading(true);
    setSelectValue(value);
  };

  return (
    <SearchInput
      {...searchProps}
      valueToShow={valueToShow}
      emptyOption={emptyOption}
      placeholder={placeholder}
      optionListDisabled={optionListDisabled}
      options={keyName && Array.isArray(optionList) ? optionList?.map((v) => v[keyName]) : optionList}
      onChange={onSelectChange}
      onChangeInputText={onChangeInputText}
      isLoading={isLoading}
      dataCy={dataCy}
      autoComplete={autoComplete}
      invalid={invalid}
      ariaLabel={ariaLabel}
      errorMsgHolderId={errorMsgHolderId}
    />
  );
};

export default RemoteSearchSelect;
